import React from 'react';

import { cn } from '@/lib/utils';

function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        // Higher-contrast base + brighter sweep so the shimmer reads as a
        // loading state at a glance (Vercel AI SDK / ChatGPT pattern).
        'relative overflow-hidden rounded-md bg-muted',
        'before:absolute before:inset-0',
        'before:-translate-x-full',
        'before:animate-[shimmer_1.6s_infinite]',
        'before:bg-gradient-to-r',
        'before:from-transparent before:via-foreground/15 before:to-transparent',
        'dark:before:via-foreground/10',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SkeletonGrid: A reusable skeleton grid for loading states.
 *
 * Props:
 * - count: number of skeleton items to render (default: 6)
 * - columns: number of columns in the grid (default: responsive 1/2/3)
 * - renderItem: function to render each skeleton item (receives index)
 * - className: additional classes for the grid container
 * - itemClassName: additional classes for each skeleton item
 *
 * Usage:
 * <SkeletonGrid count={8} columns="grid-cols-1 md:grid-cols-2 lg:grid-cols-4" renderItem={i => <Skeleton className="h-32 w-full" />} />
 */
export function SkeletonGrid({
  count = 6,
  columns = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  renderItem,
  className = '',
  itemClassName = '',
}: {
  count?: number;
  columns?: string;
  renderItem?: (i: number) => React.ReactNode;
  className?: string;
  itemClassName?: string;
}): JSX.Element {
  return (
    <div className={cn('grid gap-4', columns, className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={itemClassName}>
          {renderItem ? renderItem(i) : <Skeleton className="h-32 w-full" />}
        </div>
      ))}
    </div>
  );
}

export { Skeleton };
